import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { CollapsibleAlert, InputRow } from 'components/form'
import Screen from 'components/layout/Screen'
import SpinnerFull from 'components/SpinnerFull'
import { cloneDeep, map, omit } from 'lodash-es'
import { useEffect, useState } from 'react'
import { Card, CardColumns, Col, Form, ListGroup, Row } from 'react-bootstrap'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import API from 'state/API'
import { useUser } from 'state/Auth'
import { PRIVILEGES } from 'state/Constants'
import { editItem, editWildcardState, fetchItem, getCurrentItem, useItemIcons } from 'state/Items'
import MenuItem from 'state/models/MenuItem'
import { Alerts, useBoolState, useCurrentEntry } from 'utils'
import PricesCard from './PricesCard'
import './styles.scss'

/*
 *
 *
 *
 * FIXME: https://app.shortcut.com/ehungry/story/33385/menu-edit-delete-not-working#activity-34380
 *
 *
 *
 */
export default function ItemPage() {
	const { id }   = useParams()
	const dispatch = useDispatch()
	const icons    = useItemIcons()
	const { t }    = useTranslation()
	let item       = useCurrentEntry(getCurrentItem, fetchItem)

	//region Form setup
	// RHF bugs in some weird cases and the only thing that could make it work was changing something on the state, so we created a "fake state" to "unbug" it
	const [wildcard, setWildcard] = useState(false)

	//2. prepares the form with whatever information we got, if any
	//TODO: add shouldUseNativeValidation and style invalid fields properly
	const form = useForm({ criteriaMode: 'all', defaultValues: item, mode: 'onChange' })
	// const { isDirty } = form.formState //needs to be explicitly defined, otherwise will silently fail https://is.gd/Tssj9M

	//3. stores a tiny state to know when we got the final item details, and resets the form accordingly
	const [fullyLoaded, setLoaded] = useState(false)
	if (item && !fullyLoaded) { //ok, now that we have the full item payload, let's finally refill the form, if needed
		if (form.getValues('id') != item.id) {
			form.reset(item) //TODO we should trigger validation right here, in case some server info is considered invalid by our form, but... trigger() doesn't seem to do the trick?
		}
		setLoaded(true)
	}

	//4. watch prices so, when all of them get hidden, we can force-hide the item too
	const allPricesHidden = useBoolState(false)
	const pricesWatcher = form.watch('menu_prices')
	if (allPricesHidden.isnt) {
		if (pricesWatcher?.every(menu => menu.prices?.every(price => !price.is_shown))) {
			allPricesHidden.on()
			form.setValue('status', MenuItem.STATUS.HIDDEN, { shouldDirty: true })
		}
	} else {
		if (pricesWatcher?.some(menu => menu.prices?.some(price => price.is_shown))) {
			allPricesHidden.off()
		}
	}

	let statusBlocked = false
	if (item && !item.has_category) { //if item is not present yet, just skip
		statusBlocked = t("This item won't show for customers since it has no category to be listed at.")
	} else if (allPricesHidden.is) {
		statusBlocked = t('Activate or add a price to be able to display this item.')
	}

	let [filled, changeFilled] = useState(item?.display_name)
	//endregion

	async function onSubmit() {
		let wasUpdated = false

		// TODO nice little feature but doesn't seem to sniff deep enough, price status changes doesn't seem to affect this?
		// if (!isDirty) {
		// 	console.warn('no dirty fields, so there is no reason to submit the form.... right?', form.formState.isDirty, form.formState.dirtyFields)
		// 	return false
		// }

		setWildcard(true) //FIXME if this is used for controlling submit state, rename it!!!!!!
		await form.handleSubmit(
			async (data) => {
				let payload = cloneDeep(data) //otherwise, RHF keeps messing up with `data` while we clean it up

				if (icons.length) {
					//Registered checkboxes were bugging so hard with the defaultValues pre-created on RHF hooks that I needed to create another prop to save the checked icons
					payload.icon_ids = Array.isArray(payload.checked_icons)? payload.checked_icons :
						(typeof payload.checked_icons === 'boolean')? [] : payload.checked_icons.split()
					payload = omit(payload, ['checked_icons'])
				}

				if (!form.formState.dirtyFields.hasOwnProperty('shown_status')) {
					delete payload.shown_status //no need to submit if it hasn't changed
				}

				//clear some unused menu/price data in the ITEM endpoint
				payload.menu_prices = payload.menu_prices
					.map(menu => ({
						id    : menu.id,
						prices: menu.prices
							.filter(price => price.price_value != undefined)        //clear out empty prices...
							.map(price => omit(price, ['menu_id', 'text_price'])),  //...and redundant/useless fields
					}))
					.filter(menu => menu.prices.length) //then, we don't even need to send empty menu entries, right?

				//submit the actual data
				const result = await dispatch(editItem(id, payload))

				//updates the local item with whatever the server sends, so the form can be reset later (i.e. with sanitized inputs)
				if (result) {
					item = result
					wasUpdated = true
					setTimeout(() => dispatch(editWildcardState(true)), 2000)
				} else {
					throw new Error('Something bad happened when updating the item. Check previous logs?')
				}
			},
			validationErrors => {
				const totalErrors = Object.keys(validationErrors).length
				if (totalErrors) {
					Alerts.warning(t('There are {{count}} invalid fields. Check the form and try again.', { count: totalErrors }))
				}
			}
		)().catch((e) => console.error(e))

		setTimeout(() => setWildcard(false), 3200)
		return wasUpdated
	}

	useEffect(() => { //we can't reset() inside onSubmit(), so we use a separate effect: https://is.gd/c4qpRS
		if (form.formState.isSubmitSuccessful) {
			form.reset(item) //by this time, item will be refreshed from onSubmit changes
		}
	}, [form, item])

	useEffect(() => { // this WAY we can use the React hook form validation and disable the submit button when display_name is empty
		if (form.formState.errors.display_name) {
			changeFilled(false)
			return
		}
		changeFilled(true)
	}, [form, changeFilled])

	//must stay here, after all the other hooks - which can't be optional zzzzzz
	const notAllowed = useUser().useFullPagePrivilege(PRIVILEGES().MENU_UPDATE, { page: 'Menu Item', id })
	if (notAllowed) {
		return notAllowed
	}

	// t('Display name:')
	// t('Description:')
	return <Screen back title={item? item.display_name : t("Loading item details...")}>
		<Screen.Content addPadding={!!item}>
		{!item?
			<SpinnerFull icon="pencil-alt" resource={API.RESOURCES.ITEMS}/>
		:
			<FormProvider {...form}>
				<CardColumns>
					<Card>
						<Card.Header><Icon icon="pencil-alt"/> {t('Item Details')}</Card.Header>
						<ListGroup variant="flush">
							<ListGroup.Item>
								<InputRow name="display_name" required maxLength={90}/>
								<InputRow name="description" type="textarea" rows={5} maxLength={500}/>

								<Row id="icons">
									{icons?.map((icon, i) => (
										<Col key={i} xs={6} sm={4} md={4} lg={3} className="mb-2 pr-1 text-nowrap I-need-checkboxes-here">
											<Form.Check inline type="checkbox" id={`inline-checkbox-${icon.id}`}
											            {...form.register('checked_icons')}
											            value={icon.id} defaultChecked={item.icon_ids.includes(icon.id)}
											            label={<img src={`${icon.url}`} alt={icon.name}/>}
											/>
										</Col>
									))}
								</Row>
							</ListGroup.Item>

							<ListGroup.Item>
								<Form.Group>
									<Form.Label>{t('Status:', { context: 'menu item' })}</Form.Label>
									<CollapsibleAlert in={!!statusBlocked} children={statusBlocked}/>
									{map(MenuItem.STATUS_UI, ({ icon, className: color, text }, key) => (
										<Form.Check type="radio" id={`status-${key}`} key={key} value={key}
										            {...form.register('shown_status')} disabled={!!statusBlocked}
										            className={!!statusBlocked && 'text-muted'}
										            label={<><span className={!!statusBlocked? '' : color}><Icon fixedWidth icon={icon}/></span> {text}</>}/>
									))}
								</Form.Group>
							</ListGroup.Item>
						</ListGroup>
					</Card>

					<PricesCard {...{ wildcard, form, item }}/>
				</CardColumns>
			</FormProvider>
		}
		</Screen.Content>
		<Screen.SaveFooter disabled={!filled} action={onSubmit}/>
	</Screen>
}
